/*
 * <gallery-dots> — pagination dots for the product media slider on mobile.
 * One dot per visible slide of the slider named in data-slider; the active dot
 * follows the scroll position and a tap on a dot scrolls to its slide.
 * The slides can change (variant change adds, removes and reorders media), so
 * the dots are rebuilt whenever the slider's children change.
 */
class GalleryDots extends HTMLElement {
  connectedCallback() {
    this.slider = document.getElementById(this.dataset.slider);
    if (!this.slider) return;

    this.onScroll = () => this.update();
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });

    this.observer = new MutationObserver(() => this.build());
    this.observer.observe(this.slider, { childList: true });

    this.onResize = () => this.build();
    window.addEventListener('resize', this.onResize, { passive: true });

    this.build();
  }

  disconnectedCallback() {
    if (this.slider) this.slider.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  // Slides hidden for the current variant have no width
  slides() {
    return Array.from(this.slider.children).filter((slide) => slide.clientWidth > 0);
  }

  build() {
    const slides = this.slides();
    // Nothing to rebuild when the slider is not displayed (desktop) or the count is the same
    if (slides.length === this.children.length && slides.length > 1) {
      this.update();
      return;
    }

    this.hidden = slides.length < 2;
    this.innerHTML = this.hidden
      ? ''
      : slides
          .map(
            (_, index) =>
              `<button type="button" class="gallery-dots__dot" aria-label="${index + 1} / ${slides.length}"></button>`
          )
          .join('');

    this.querySelectorAll('button').forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const slide = this.slides()[index];
        if (slide) this.slider.scrollTo({ left: slide.offsetLeft - this.slides()[0].offsetLeft, behavior: 'smooth' });
      });
    });

    this.update();
  }

  update() {
    const slides = this.slides();
    if (!slides.length) return;

    const origin = slides[0].offsetLeft;
    let active = 0;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - origin - this.slider.scrollLeft);
      if (distance < Math.abs(slides[active].offsetLeft - origin - this.slider.scrollLeft)) active = index;
    });

    Array.from(this.children).forEach((dot, index) => {
      dot.classList.toggle('is-active', index === active);
      dot.toggleAttribute('aria-current', index === active);
    });
  }
}

if (!customElements.get('gallery-dots')) {
  customElements.define('gallery-dots', GalleryDots);
}
